import { formatDuration } from '@krp/ui-kit'

import type { SearchTiming, SearchTimingSummary, StageOutcome, TimingStats, TimingsLoad } from '../model/types'

/** Названия этапов для человека; неизвестный этап (новый на сервере) показывается своим кодом, а не прячется. */
const STAGE_TEXT: Record<string, string> = {
  read: 'Чтение текста',
  signature: 'Сигнатура кадра',
  visual: 'Поиск по виду',
  barcode: 'Штрихкод',
  text: 'Поиск по тексту',
  merge: 'Слияние дорожек',
  catalog: 'Каталог',
  prepare: 'Подготовка поиска',
  judge: 'Проверка моделью',
}
const stageText = (stage: string): string => STAGE_TEXT[stage] ?? stage

/** Исход — словом: состояние не только цветом. */
const OUTCOME_TEXT: Record<StageOutcome, string> = { ok: 'готово', failed: 'ошибка', skipped: 'пропущено' }

function Notice({ load, label }: { load: TimingsLoad<unknown>; label: string }) {
  if (load.status === 'loading') return <p className="platform-search__timings-note">Загружаем {label}…</p>
  if (load.status === 'missing') return <p className="platform-search__timings-note">Для этого поиска времени нет: он не записан или срок хранения истёк.</p>
  if (load.status === 'error') {
    return (
      <p className="platform-search__timings-note" role="alert">
        {load.message}
        {load.correlationId === null ? '' : ` · запрос ${load.correlationId}`}
      </p>
    )
  }
  return null
}

/** Время этого поиска: итог, первый список и по этапам — начало, длительность и исход. */
function CurrentTiming({ timing }: { timing: SearchTiming }) {
  const stages = [...timing.stages].sort((left, right) => left.startMs - right.startMs)
  return (
    <>
      <p className="platform-search__timings-note">
        До итога {formatDuration(timing.totalMs)}, до первого списка {formatDuration(timing.firstListMs)}
      </p>
      <table className="platform-search__timings-table" aria-label="Время этапов этого поиска">
        <thead>
          <tr>
            <th scope="col">Этап</th>
            <th scope="col">Начало</th>
            <th scope="col">Длилось</th>
            <th scope="col">Исход</th>
          </tr>
        </thead>
        <tbody>
          {stages.map((stage, index) => (
            <tr key={`${stage.stage}-${index}`} data-outcome={stage.outcome}>
              <th scope="row">{stageText(stage.stage)}</th>
              <td>{formatDuration(stage.startMs)}</td>
              <td>{stage.outcome === 'skipped' ? '—' : formatDuration(stage.durationMs)}</td>
              <td>{OUTCOME_TEXT[stage.outcome]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

const statsCells = (stats: TimingStats | null): [string, string, string, string] =>
  stats === null ? ['—', '—', '—', '0'] : [formatDuration(stats.medianMs), formatDuration(stats.p90Ms), formatDuration(stats.maxMs), String(stats.count)]

/** Сводка за период: по каждому этапу медиана, p90, максимум и число замеров; строки «до первого списка» и «до итога». */
function SummaryTable({ summary }: { summary: SearchTimingSummary }) {
  if (summary.searches === 0) return <p className="platform-search__timings-note">За период записанных поисков нет.</p>
  const rows: { key: string; title: string; stats: TimingStats | null }[] = [
    { key: 'first', title: 'До первого списка', stats: summary.firstList },
    { key: 'total', title: 'До итога', stats: summary.total },
    ...summary.stages.map((item) => ({ key: item.stage, title: stageText(item.stage), stats: item.stats })),
  ]
  return (
    <>
      <p className="platform-search__timings-note">
        Поисков: {summary.searches}
        {summary.truncated ? ' (учтены последние, за период их больше)' : ''}
      </p>
      <table className="platform-search__timings-table" aria-label="Сводка времени этапов за период">
        <thead>
          <tr>
            <th scope="col">Этап</th>
            <th scope="col">Медиана</th>
            <th scope="col">p90</th>
            <th scope="col">Максимум</th>
            <th scope="col">Замеров</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const [median, p90, max, count] = statsCells(row.stats)
            return (
              <tr key={row.key}>
                <th scope="row">{row.title}</th>
                <td>{median}</td>
                <td>{p90}</td>
                <td>{max}</td>
                <td>{count}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </>
  )
}

/**
 * Время поиска по этапам (только администратору, внутри «Посмотреть детали поиска»): строка этого поиска, если у него есть номер, и сводка за
 * последние 7 дней. Данные приносит приложение (`current`/`summary`), здесь только показ; `current: null` — у поиска нет номера в журнале.
 */
export function StageTimingsView({ current, summary }: { current: TimingsLoad<SearchTiming> | null; summary: TimingsLoad<SearchTimingSummary> }) {
  return (
    <section className="platform-search__timings" role="region" aria-label="Время по этапам">
      <p className="platform-search__admin-title">Время по этапам</p>
      {current === null ? (
        <p className="platform-search__timings-note">У этого поиска нет номера в журнале, разбивки по этапам нет.</p>
      ) : current.status === 'ready' ? (
        <CurrentTiming timing={current.data} />
      ) : (
        <Notice load={current} label="время этого поиска" />
      )}
      <p className="platform-search__admin-title">Сводка за 7 дней</p>
      {summary.status === 'ready' ? <SummaryTable summary={summary.data} /> : <Notice load={summary} label="сводку" />}
    </section>
  )
}
